import React, { memo } from 'react';

import { SETTING_ACTION_TYPE } from '../../../../constants/settingsForm';

import Toggle from '../../../../design-system/Toggle/Toggle';
import RichTextArea from '../../../../design-system/RichTextArea/RichTextArea';
import JsonTextareaBox from '../../../../design-system/JsonTextareaBox/JsonTextareaBox';

import FileIcon from '../../../../design-system/Icons/FileIcon';
import ViewOnlyNestedInput from '../ViewOnlyNestedInput/ViewOnlyNestedInput';
import { Badge } from '../../../../design-system';

const SettingsViewOnlyForm = ({ settings = [], emptyValueMessage = 'Not filled' }) => {
    if (!settings?.length) return null;

    return (
        <div className="flex flex-col gap-6">
            {settings.map((item) => {
                const { name, label, action } = item;

                return (
                    <div key={name} className="flex flex-col gap-3">
                        {action !== SETTING_ACTION_TYPE.toggle && (
                            <p className="font-body-bold text-body-bold-l text-neutral-500">
                                {label}
                            </p>
                        )}

                        <SettingViewOnlyValue item={item} emptyValueMessage={emptyValueMessage} />
                    </div>
                );
            })}
        </div>
    );
};

function SettingViewOnlyValue({ item, emptyValueMessage }) {
    const { action, label, value, options } = item;

    if (action === SETTING_ACTION_TYPE.toggle) {
        return (
            <Toggle
                label={label}
                isSelected={value === true}
                size="sm"
                onChange={() => {}}
                customLabelClassName="!text-body-bold-l"
            />
        );
    }

    const isEmpty = !value || (Array.isArray(value) && !value.length);

    if (isEmpty) {
        return (
            <p className="font-body text-body-regular-m text-neutral-300 italic">
                {emptyValueMessage}
            </p>
        );
    }

    if (action === SETTING_ACTION_TYPE.richtext_input) {
        return (
            <RichTextArea
                value={value}
                previewOnly
                autoExpand
                isBorderHidden
                previewMinHeight={30}
            />
        );
    }

    if (action === SETTING_ACTION_TYPE.json_input) {
        return (
            <div className="flex flex-col gap-4">
                <JsonTextareaBox mode="view" value={value} containerMinHeight="auto" />
            </div>
        );
    }

    if (action === SETTING_ACTION_TYPE.code_input) {
        return (
            <RichTextArea
                value={'```\n' + value + '\n```'}
                previewOnly
                autoExpand
                isBorderHidden
                previewMinHeight={30}
            />
        );
    }

    if ([SETTING_ACTION_TYPE.single_file, SETTING_ACTION_TYPE.multi_file].includes(action)) {
        let filesArray = [];

        if (action === SETTING_ACTION_TYPE.single_file && value && typeof value === 'string') {
            filesArray.push(value);
        }

        if (action === SETTING_ACTION_TYPE.multi_file && Array.isArray(value) && !!value.length) {
            for (const file of value) {
                if (typeof file === 'string') {
                    filesArray.push(file);
                }
            }
        }

        return (
            <div className="w-full min-h-[56px] px-4 py-3 flex items-center justify-start rounded-2 border border-dashed border-purple-300 bg-purple-100 bg-opacity-30 cursor-default">
                <div className="flex items-center gap-x-2 gap-y-1 flex-wrap max-w-full">
                    {filesArray.map((filePath, index) => (
                        <div key={index} className="max-w-full">
                            <Badge
                                text={filePath}
                                color="purple"
                                leadingIcon={FileIcon}
                                badgeContentColor="#754DCF"
                                customTextStyles="-ml-1"
                            />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (action === SETTING_ACTION_TYPE.nested_input) {
        return <ViewOnlyNestedInput setting={item} />;
    }

    let formattedValue = null;

    if (action === SETTING_ACTION_TYPE.single_db) {
        const { label } = value || {};
        formattedValue = label;
    }

    if (action === SETTING_ACTION_TYPE.multi_db) {
        formattedValue = value.map(({ label }) => label);
    }

    if (
        [
            SETTING_ACTION_TYPE.single_input,
            SETTING_ACTION_TYPE.textarea_input,
            SETTING_ACTION_TYPE.multi_input,
        ].includes(action)
    ) {
        formattedValue = value;
    }

    if ([SETTING_ACTION_TYPE.single_select, SETTING_ACTION_TYPE.single_dropdown].includes(action)) {
        formattedValue = Object.keys(options || {}).find((key) => options[key] === value);
    }

    if ([SETTING_ACTION_TYPE.multi_select, SETTING_ACTION_TYPE.multi_dropdown].includes(action)) {
        formattedValue = Object.keys(options || {}).filter((key) => value.includes(options[key]));
    }

    if (formattedValue && ['string', 'number'].includes(typeof formattedValue)) {
        return (
            <p className="font-body text-body-regular-m text-neutral-500 leading-[150%] whitespace-pre-wrap">
                {formattedValue}
            </p>
        );
    }

    if (formattedValue && Array.isArray(formattedValue) && !!formattedValue.length) {
        return (
            <ul className="flex flex-col gap-1 list-disc list-outside pl-5">
                {formattedValue.map((piece, index) => (
                    <li
                        key={index}
                        className="font-body text-body-regular-m text-neutral-500 leading-[150%] whitespace-pre-wrap"
                    >
                        {piece}
                    </li>
                ))}
            </ul>
        );
    }

    return null;
}

export default memo(SettingsViewOnlyForm);
